import * as React from "react";
import * as ReactDOM from "react-dom";
import { Alert, Button, Checkbox, Input, notification } from "antd";
import { Loader } from "../../../components/layout/loader";
import { List } from "react-window";
import { request } from "../../../services/cpln";
import { Tooltip } from "../../../components/Tooltip";
import { BROWSER_REL, BrowserRes } from "../../../store/slices/browserServiceSlice";

export interface BrowserRole {
  label: string;
  ref: string;
}

interface DataItemProps {
  label: string;
  onClick: any;
  isActive: boolean;
  style: any;
}
export const SelectRolesModalDataItem: React.FC<DataItemProps> = ({ label, onClick, isActive, style }) => {
  return (
    <div className="flex items-center text-sm" onClick={onClick} style={style}>
      <Checkbox checked={isActive} />
      <Tooltip title={label} open={label.length >= 63}>
        <div className={`browser-service-item ml-1 cursor-pointer   truncate ${isActive ? "   " : ""}`}>{label}</div>
      </Tooltip>
    </div>
  );
};

interface Props {
  onClose: (selections: string[]) => void;
  itemHref: string;
  currentSelections: string[];
  isEditing: boolean;
  type: "scope" | "resource";
}
export const SelectRolesModal: React.FC<Props> = ({ onClose, itemHref, currentSelections, isEditing, type }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [selections, setSelections] = React.useState<string[]>([...currentSelections]);
  const [error, setError] = React.useState("");
  const [_roles, setRoles] = React.useState<BrowserRole[]>([]);

  // Sync selections when currentSelections changes (e.g., when editing)
  const currentSelectionsKey = currentSelections.join(",");
  React.useEffect(() => {
    setSelections([...currentSelections]);
  }, [currentSelectionsKey]);

  React.useEffect(() => {
    if (itemHref) {
      setIsLoading(true);
      request<BrowserRes>({ service: "browser", url: `${itemHref}` })
        .then(async ({ data }) => {
          const rolesLink = data.links.find((l) => l.rel === BROWSER_REL.roles)!.href;
          let allRoles: BrowserRole[] = [];
          let nextLink: string | undefined = rolesLink;
          while (nextLink) {
            const res = await request<BrowserRes>({ service: "browser", url: nextLink });
            allRoles = allRoles.concat((res.data.data || []).map((d) => ({ label: d._cpln.label, ref: d._cpln.ref })));
            nextLink = res.data.links?.find((l) => l.rel === "next")?.href;
          }
          setRoles(allRoles);
          setIsLoading(false);
        })
        .catch((e) => {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          console.log("e?.response?.data?.message", e?.response?.data?.message);
          console.log("e.message", e.message);
          setError(errorMessage);
          setIsLoading(false);
          notification.warning({ message: "Failed", description: errorMessage });
        });
    }
  }, []);

  function onItem(role: BrowserRole) {
    if (selections.includes(role.ref)) {
      setSelections(selections.filter((s) => s !== role.ref));
    } else {
      setSelections([...selections, role.ref]);
    }
  }

  let filteredRoles = _roles.filter((role) => role.label.toLowerCase().includes(search));

  const unmatchedSelections = React.useMemo(() => {
    if (selections.length === 0 || _roles.length === 0) return [];
    const roleRefSet = new Set(_roles.map((r) => r.ref));
    return selections.filter((s) => !roleRefSet.has(s));
  }, [selections, _roles]);

  return ReactDOM.createPortal(
    <div
      className="fixed w-screen h-screen inset-0 flex items-center justify-center"
      style={{ backgroundColor: "rgb(0, 0, 0, 0.5)", zIndex: 1200 }}
    >
      <div
        className="text-normal"
        style={{ backgroundColor: "var(--color-drop)", padding: "20px 24px", borderRadius: "8px" }}
      >
        {error || !itemHref ? (
          <>
            <Alert
              type={"error"}
              message={
                itemHref
                  ? error
                  : isEditing
                  ? `Please select the ${type} again by browsing it to be able to browse roles`
                  : `There is no ${type} to browse roles of`
              }
            />
            <div className="flex justify-end mt-4">
              <Button type={"primary"} className="ml-4" onClick={() => onClose(selections)}>
                OK
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">Select Roles</div>
            {unmatchedSelections.length > 0 && (
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message="Unrecognized Roles"
                description={
                  <div>
                    <div>The following roles were not found:</div>
                    <ul className="mt-1 mb-0 pl-4">
                      {unmatchedSelections.map((ref) => (
                        <li key={ref}>
                          <code>{ref}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                }
              />
            )}
            <div>Search</div>
            <Input name="search" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div onClick={(e) => e.stopPropagation()} style={{ width: 500, height: 400 }} className="flex">
              <div style={{ width: 500, height: 400 }} className="overflow-auto">
                {isLoading ? null : filteredRoles.length < 1 ? (
                  <span>No Roles Found</span>
                ) : (
                  <List
                    rowProps={{ roles: filteredRoles }}
                    rowComponent={({ index, roles, style }) => {
                      const role = roles[index];
                      return (
                        <SelectRolesModalDataItem
                          style={style}
                          onClick={() => onItem(role)}
                          isActive={selections.includes(role.ref)}
                          label={role.label!}
                        />
                      );
                    }}
                    rowCount={filteredRoles.length}
                    rowHeight={22}
                  />
                )}
              </div>
            </div>
            {isLoading ? <Loader fullScreen={false} /> : null}
            <div className="flex justify-end mt-4">
              <Button type={"default"} danger onClick={() => onClose(currentSelections)}>
                Cancel
              </Button>
              <Button
                type={"primary"}
                className="action ml-4"
                onClick={() => onClose(selections)}
                disabled={selections.length < 1}
              >
                Confirm
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body!,
  );
};
